import { createPlaceReview } from "@/api/places";
import StarRatingPicker from "@/components/upload-comps/notes-form-fields-section/notes-more-settings-section/star-rating-picker";
import H4 from "@/components/ui/headings/H4";
import H5 from "@/components/ui/headings/H5";
import H6 from "@/components/ui/headings/H6";
import Iconify from "@/components/ui/Iconify";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { useTranslations } from "@/lib/static-intl";
import { enqueueSnackbar } from "notistack";
import { useRef, useState } from "react";
import useScrollOnDrag from "react-scroll-ondrag";

const ReviewPostForm = ({
  noteDetails,
  setNoteDetails,
  placeId,
  onClose,
  resetModal,
}) => {
  const t = useTranslations("creator_service.creator_form");
  const t2 = useTranslations("places.place_review_create_modal");
  const apiT = useTranslations("api");

  const ref = useRef();
  const { user } = useAuthContext();
  const { events } = useScrollOnDrag(ref);

  const [loader, setLoader] = useState(false);

  const handleMediaUpload = (event) => {
    const files = Array.from(event.target.files);

    if (!files.length) return;

    const isVideo = files.some((file) => file.type.startsWith("video/"));
    const isImage = files.some((file) => file.type.startsWith("image/"));

    if (isVideo && isImage) {
      enqueueSnackbar(t2("upload_either_video_or_image"), {
        variant: "warning",
      });
      return;
    }

    if (isImage) {
      const validImages = files.filter((file) =>
        ["image/jpeg", "image/png", "image/webp"].includes(file.type)
      );

      if (noteDetails.media.length + validImages.length > 21) {
        enqueueSnackbar(t2("upload_max_21_img"), {
          variant: "warning",
        });
        return;
      }

      setNoteDetails((prev) => ({
        ...prev,
        media: [...prev.media, ...validImages],
      }));
    }

    if (isVideo) {
      if (files.length > 1) {
        enqueueSnackbar(t2("upload_max_1_video"), {
          variant: "warning",
        });
        return;
      }

      if (!["video/mp4", "video/webm"].includes(files[0].type)) {
        enqueueSnackbar(t2("video_formats_available"), {
          variant: "warning",
        });
        return;
      }

      setNoteDetails((prev) => ({
        ...prev,
        media: [files[0]],
      }));
    }
  };

  const removeMedia = (index) => {
    setNoteDetails((prev) => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index),
    }));
  };

  const replaceMedia = (index) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png, image/jpeg, image/webp, video/mp4, video/webm";
    input.onchange = (event) => {
      const file = event.target.files[0];

      if (!file) return;

      const isVideo = file.type.startsWith("video/");
      const isImage = file.type.startsWith("image/");

      // Ensure replacing with a valid file type
      if (
        isVideo &&
        noteDetails.media.some((m) => m.type.startsWith("image/"))
      ) {
        enqueueSnackbar(t2("cannot_replace_img_with_video"), {
          variant: "warning",
        });
        return;
      }

      if (
        isImage &&
        noteDetails.media.some((m) => m.type.startsWith("video/"))
      ) {
        enqueueSnackbar(t2("cannot_replace_video_with_img"), {
          variant: "warning",
        });
        return;
      }

      setNoteDetails((prev) => {
        const newMedia = [...prev.media];
        newMedia[index] = file;
        return { ...prev, media: newMedia };
      });
    };
    input.click();
  };

  const uploadMoreMedia = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png, image/jpeg, image/webp, video/mp4, video/webm";
    input.multiple = true;
    input.onchange = (event) => {
      const files = Array.from(event.target.files);

      if (!files.length) return;

      const isVideo = files.some((file) => file.type.startsWith("video/"));
      const isImage = files.some((file) => file.type.startsWith("image/"));

      // Prevent mixing images and videos
      if (isVideo && isImage) {
        enqueueSnackbar(t2("upload_either_video_or_image"), {
          variant: "warning",
        });
        return;
      }

      if (isImage) {
        const validImages = files.filter((file) =>
          ["image/jpeg", "image/png", "image/webp"].includes(file.type)
        );

        if (noteDetails.media.length + validImages.length > 21) {
          enqueueSnackbar(t2("upload_max_21_img"), {
            variant: "warning",
          });
          return;
        }

        setNoteDetails((prev) => ({
          ...prev,
          media: [...prev.media, ...validImages],
        }));
      }

      if (isVideo) {
        if (files.length > 1) {
          enqueueSnackbar(t2("upload_max_1_video"), {
            variant: "warning",
          });
          return;
        }

        if (!["video/mp4", "video/webm"].includes(files[0].type)) {
          enqueueSnackbar(t2("video_formats_available"), {
            variant: "warning",
          });
          return;
        }

        setNoteDetails((prev) => ({
          ...prev,
          media: [files[0]],
        }));
      }
    };
    input.click();
  };

  const clearAllMedia = () => {
    setNoteDetails((prev) => ({
      ...prev,
      media: [],
    }));
  };

  const handleSubmit = async () => {
    try {
      if (user?.userId && noteDetails?.title?.length === 0) {
        enqueueSnackbar(t2("enter_a_title"), { variant: "error" });
        return;
      }

      if (noteDetails?.media?.length === 0) {
        enqueueSnackbar(t2("upload_a_media"), { variant: "error" });
        return;
      }

      if (noteDetails?.rating === 0) {
        enqueueSnackbar(t2("rate_this_place"), { variant: "error" });
        return;
      }

      if (!user?.userId && noteDetails.username.length === 0) {
        enqueueSnackbar(t2("enter_a_username"), { variant: "error" });
        return;
      }

      setLoader(true);
      const formData = new FormData();

      formData.append("placeId", placeId);
      if (user?.userId) formData.append("userId", user?.userId);

      formData.append("username", noteDetails.username);
      formData.append("review", noteDetails.review);

      noteDetails.media.forEach((media) => {
        formData.append("media", media);
      });

      formData.append("title", noteDetails.title);
      formData.append("description", noteDetails.description);
      formData.append("rating", noteDetails.rating);
      formData.append("status", noteDetails.status);
      formData.append("isPinned", noteDetails.isPinned);
      formData.append("isPublic", noteDetails.isPublic);
      if (noteDetails.isTimelyRelease) {
        formData.append("releaseTime", noteDetails.releaseTime);
      }

      const response = await createPlaceReview(formData);
      console.log(response);
      resetModal();
      onClose();
      setNoteDetails({
        title: "",
        description: "",
        username: "",
        review: "",
        media: [],
        rating: 0,
        status: "published",
        isPinned: false,
        isPublic: false,
        isTimelyRelease: false,
        releaseTime: null,
      });
      enqueueSnackbar(t2("review_submitted_successfully"), {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    } finally {
      setLoader(false);
    }
  };
  return (
    <>
      <div className="w-full h-full max-h-full overflow-y-auto overflow-x-hidden thin-scrollbar py-5 px-5 md:px-10 flex flex-col gap-2">
        {noteDetails.media.length > 0 && (
          <div className="w-full flex flex-col gap-2">
            <div className="w-full flex items-end justify-between">
              <H4 className={"tracking-tight text-xl !font-bold"}>
                {noteDetails.media.some((m) => m.type.startsWith("image/"))
                  ? t2("photos")
                  : t2("videos")}
              </H4>
              <div className="w-fit flex gap-4">
                {noteDetails.media.some((m) => m.type.startsWith("image/")) && (
                  <button
                    type="button"
                    className="text-xs text-red text-nowrap underline"
                    onClick={uploadMoreMedia}
                  >
                    {t2("upload_mode")}
                  </button>
                )}
                <button
                  type="button"
                  className="text-xs text-red text-nowrap underline"
                  onClick={clearAllMedia}
                >
                  {t2("clear_all")}
                </button>
              </div>
            </div>
            <div
              ref={ref}
              {...events}
              className="w-full max-w-full flex items-center gap-4 overflow-x-auto thin-scrollbar pb-2"
            >
              {noteDetails.media.map((media, index) => (
                <div
                  key={index}
                  className="min-w-[120px] w-[120px] h-[120px] border dark:border-brown rounded overflow-hidden select-none relative flex items-center justify-center group"
                >
                  <div className="absolute z-20 top-0 left-0 right-0 bottom-0 rounded-sm flex items-end invisible group-hover:visible bg-transparent transition">
                    <div className="w-full bg-black/50 backdrop-blur text-white text-[11px] p-1 flex items-center justify-center gap-3">
                      <button
                        type="button"
                        className="text-inherit text-nowrap"
                        onClick={() => removeMedia(index)}
                      >
                        {t2("remove")}
                      </button>
                      <span className="text-inherit">|</span>
                      <button
                        type="button"
                        className="text-inherit text-nowrap"
                        onClick={() => replaceMedia(index)}
                      >
                        {t2("replace")}
                      </button>
                    </div>
                  </div>
                  {media?.type?.startsWith("video/") && (
                    <div className="absolute z-10 top-0 left-0 right-0 bottom-0 flex items-center justify-center">
                      <Iconify
                        icon={"iconoir:play-solid"}
                        className={"!text-white !w-7 !h-7"}
                      />
                    </div>
                  )}
                  {media?.type?.startsWith("video/") ? (
                    <video
                      src={URL.createObjectURL(media)}
                      className="w-full h-full object-contain"
                      controls={false}
                      muted
                    />
                  ) : (
                    <img
                      src={URL.createObjectURL(media)}
                      alt="notes"
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="w-full flex flex-col gap-2">
          <H4 className={"tracking-tight text-xl !font-bold"}>
            {t("Content")}
          </H4>
          {user?.userId ? (
            <>
              <div className="w-full flex flex-col gap-0">
                <input
                  type="text"
                  className="w-full p-2 outline-none focus:border-ink transition duration-100 border text-sm rounded dark:bg-brown dark:border-light_primary_label"
                  placeholder={t("Fill in the title to get more likes")}
                  value={noteDetails.title}
                  onChange={(e) =>
                    setNoteDetails({
                      ...noteDetails,
                      title: e.target.value,
                    })
                  }
                />
                <span
                  className={`${
                    noteDetails.title.length > 0 ? "invisible" : "visible"
                  } text-red text-[11px] italic`}
                >
                  {t("Title cannot be empty")}
                </span>
              </div>
              <textarea
                rows={6}
                className="w-full p-2 outline-none focus:border-ink dark:bg-brown transition duration-100 border text-sm rounded resize-none dark:border-light_primary_label overflow-y-auto thin-scrollbar"
                placeholder={t("create_note_desc_placeholder")}
                value={noteDetails.description}
                onChange={(e) =>
                  setNoteDetails({
                    ...noteDetails,
                    description: e.target.value,
                  })
                }
              />
            </>
          ) : (
            <>
              <div className="w-full flex flex-col gap-0">
                <input
                  type="text"
                  className="w-full p-2 outline-none focus:border-ink transition duration-100 border text-sm rounded dark:bg-brown dark:border-light_primary_label"
                  placeholder={t2("enter_a_username")}
                  value={noteDetails.username}
                  onChange={(e) =>
                    setNoteDetails({
                      ...noteDetails,
                      username: e.target.value,
                    })
                  }
                />
                <span
                  className={`${
                    noteDetails.username.length > 0 ? "invisible" : "visible"
                  } text-red text-[11px] italic`}
                >
                  {t2("username_is_required")}
                </span>
              </div>
              <textarea
                rows={6}
                className="w-full p-2 outline-none focus:border-ink dark:bg-brown transition duration-100 border text-sm rounded resize-none dark:border-light_primary_label overflow-y-auto thin-scrollbar"
                placeholder={t2("review_placeholder")}
                value={noteDetails.review}
                onChange={(e) =>
                  setNoteDetails({
                    ...noteDetails,
                    review: e.target.value,
                  })
                }
              />
            </>
          )}
        </div>

        {noteDetails.media.length === 0 && (
          <div className="w-full flex justify-end">
            <input
              type="file"
              accept="image/png, image/jpeg, image/webp, video/mp4, video/webm"
              multiple
              onChange={handleMediaUpload}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="bg-red text-white px-3 py-2 rounded text-sm hover:brightness-95 transition cursor-pointer w-fit"
            >
              {t2("choose_image_video")}
            </label>
          </div>
        )}

        <div className="w-full flex flex-col gap-2 mt-3 md:mt-0">
          <H5 className={"text-xl !font-bold"}>{t("More Settings")}</H5>
          <div className="w-full flex flex-row items-center gap-2 md:gap-14">
            <H6>{t("Give Rating")}</H6>
            <StarRatingPicker
              initialRating={noteDetails.rating}
              onRatingChange={(r) => {
                setNoteDetails({
                  ...noteDetails,
                  rating: r,
                });
              }}
            />
          </div>
          {user?.userId && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="w-full hidden md:flex gap-2 flex-col">
                <H6 className={`text-xs font-semibold`}>
                  {t("Visibility Range")}
                </H6>
                <div className="flex flex-col gap-1">
                  <div>
                    <label className="flex items-center gap-2 text-nowrap cursor-pointer">
                      <input
                        type="radio"
                        className="accent-red"
                        checked={noteDetails.isPublic}
                        onChange={(e) => {
                          setNoteDetails({
                            ...noteDetails,
                            isPublic: e.target.checked,
                          });
                        }}
                      />
                      <H6
                        className={`transition ${
                          noteDetails.isPublic && "!text-red"
                        }`}
                      >
                        {t("Public (Visible to everyone)")}
                      </H6>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-nowrap cursor-pointer">
                      <input
                        type="radio"
                        className="accent-red"
                        checked={!noteDetails.isPublic}
                        onChange={(e) => {
                          setNoteDetails({
                            ...noteDetails,
                            isPublic: !e.target.checked,
                          });
                        }}
                      />
                      <H6
                        className={`transition ${
                          !noteDetails.isPublic && "!text-red"
                        }`}
                      >
                        {t("Private (Only visible to you)")}
                      </H6>
                    </label>
                  </div>
                </div>
              </div>
              <div className="w-full hidden md:flex gap-2 flex-col">
                <H6 className={"text-xs font-semibold"}>{t("Release Time")}</H6>
                <div className="flex flex-col gap-1">
                  <div>
                    <label className="flex items-center gap-2 text-nowrap cursor-pointer">
                      <input
                        type="radio"
                        className="accent-red"
                        checked={noteDetails.isTimelyRelease}
                        onChange={(e) => {
                          setNoteDetails({
                            ...noteDetails,
                            isTimelyRelease: e.target.checked,
                          });
                        }}
                      />
                      <H6 className={`text-red transition`}>
                        {t("For immediate release")}
                      </H6>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-nowrap cursor-pointer">
                      <input
                        type="radio"
                        className="accent-red"
                        checked={!noteDetails.isTimelyRelease}
                        onChange={(e) => {
                          setNoteDetails({
                            ...noteDetails,
                            isTimelyRelease: !e.target.checked,
                          });
                        }}
                      />
                      <H6 className={`text-red transition`}>
                        {t("Scheduled Release")}
                      </H6>
                    </label>
                  </div>
                  {!noteDetails.isTimelyRelease && (
                    <input
                      type="datetime-local"
                      className={`rounded border px-2 py-1`}
                      value={noteDetails.releaseTime}
                      onChange={(e) => {
                        setNoteDetails({
                          ...noteDetails,
                          releaseTime: e.target.value,
                        });
                      }}
                    />
                  )}
                </div>
                <div />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="w-full flex justify-between mt-auto px-8 py-5 border-t dark:border-brown bg-white dark:bg-dark_bg_grey">
        <span
          className={`text-nowrap text-xl font-semibold ${
            noteDetails.media.length === 0 ? "invisible" : "visible"
          }`}
        >
          {t2("total")} {noteDetails.media.length}{" "}
          {noteDetails.media.some((m) => m.type.startsWith("video/"))
            ? t2("videos")
            : t2("photos")}
        </span>
        <button
          type="button"
          disabled={loader}
          onClick={handleSubmit}
          className={`${
            loader ? "bg-rose-300 cursor-not-allowed" : "bg-red"
          } text-white px-3 py-2 rounded text-sm hover:brightness-95 transition flex items-center justify-center gap-2`}
        >
          {loader && (
            <span className="animate-spin">
              <Iconify
                icon={"eos-icons:three-dots-loading"}
                className={"!text-inherit"}
              />
            </span>
          )}
          <span className="text-inherit">
            {loader ? t2("posting") : t2("post_photos_video")}
          </span>
        </button>
      </div>
    </>
  );
};

export default ReviewPostForm;
